// Normalize and code-tag audit recommendations.
//
// Backend now emits { code, text } objects, but old audits stored before
// this change have plain strings. The normalizer makes both shapes safe
// to consume; ensureCode falls back to a heuristic regex match so legacy
// audits still get a "Fix" button on the audit tab.

export const REC_CODES = {
  BOT_ACCESS: 'botAccess',
  SCHEMA: 'schemaMarkup',
  FAQ: 'faqStructure',
  CAPSULES: 'answerCapsules',
  HEADINGS: 'headingHierarchy',
  LLMS_TXT: 'llmsTxt',
  CONTENT_QUALITY: 'contentQuality',
};

const KNOWN_CODES = new Set(Object.values(REC_CODES));

export function normalizeRecommendation(r) {
  if (!r) return null;
  if (typeof r === 'string') return { code: null, text: r };
  if (typeof r === 'object') {
    return {
      code: typeof r.code === 'string' ? r.code : null,
      text: typeof r.text === 'string' ? r.text : '',
    };
  }
  return null;
}

// Cheap regex matchers — only used for legacy string audits where the
// backend didn't tag a code. Order matters; specific phrases first.
const SNIFF = [
  { code: REC_CODES.SCHEMA, re: /JSON-LD schema|schema markup/i },
  { code: REC_CODES.FAQ, re: /FAQ section|FAQ blocks?|FAQ heading/i },
  { code: REC_CODES.CAPSULES, re: /Answer Capsules?/i },
  { code: REC_CODES.HEADINGS, re: /<h1>|<h2>|heading hierarchy/i },
  { code: REC_CODES.BOT_ACCESS, re: /AI crawlers|robots\.txt|bot-access/i },
  { code: REC_CODES.LLMS_TXT, re: /llms\.txt/i },
  { code: REC_CODES.CONTENT_QUALITY, re: /author byline|600\+ words|publish date/i },
];

export function inferCode(text) {
  if (!text) return null;
  for (const { code, re } of SNIFF) if (re.test(text)) return code;
  return null;
}

export function ensureCode(rec) {
  const norm = normalizeRecommendation(rec);
  if (!norm) return null;
  const code = norm.code && KNOWN_CODES.has(norm.code) ? norm.code : inferCode(norm.text);
  return { code, text: norm.text };
}

// Convenience: normalize + ensureCode + filter nulls in one pass.
export function normalizeRecommendations(list) {
  if (!Array.isArray(list)) return [];
  return list.map(ensureCode).filter(Boolean);
}
